import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

export const useSharedImageData = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Image collections for each section
  const [visualizationImages, setVisualizationImages] = useState([]);
  const [gratitudeImages, setGratitudeImages] = useState([]);
  const [avoidanceImages, setAvoidanceImages] = useState([]);
  const [injusticeImages, setInjusticeImages] = useState([]);
  const [milestoneImages, setMilestoneImages] = useState([]);

  // Load all data when component mounts
  useEffect(() => {
    loadAllImageData();
  }, []);

  const loadAllImageData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadVisualizationImages(),
        loadGratitudeImages(),
        loadAvoidanceImages(),
        loadInjusticeImages(),
        loadMilestoneImages()
      ]);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Visualization Images
  const loadVisualizationImages = async () => {
    const { data, error } = await supabase
      .from('visualization_images_shared_ms2025')
      .select('*')
      .order('created_at', { ascending: true });
    
    if (error) throw error;
    setVisualizationImages(data || []);
  };

  const addVisualizationImage = async (imageData, filename) => {
    const { data, error } = await supabase
      .from('visualization_images_shared_ms2025')
      .insert([{ image_url: imageData, filename: filename || 'image.jpg' }])
      .select();
    
    if (error) throw error;
    setVisualizationImages(prev => [...prev, ...data]);
  };

  const deleteVisualizationImage = async (id) => {
    const { error } = await supabase
      .from('visualization_images_shared_ms2025')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
    setVisualizationImages(prev => prev.filter(img => img.id !== id));
  };

  // Gratitude Images
  const loadGratitudeImages = async () => {
    const { data, error } = await supabase
      .from('gratitude_images_shared_ms2025')
      .select('*')
      .order('created_at', { ascending: true });
    
    if (error) throw error;
    setGratitudeImages(data || []);
  };

  const addGratitudeImage = async (imageData, filename) => {
    const { data, error } = await supabase
      .from('gratitude_images_shared_ms2025')
      .insert([{ image_url: imageData, filename: filename || 'image.jpg' }])
      .select();
    
    if (error) throw error;
    setGratitudeImages(prev => [...prev, ...data]);
  };

  const deleteGratitudeImage = async (id) => {
    const { error } = await supabase
      .from('gratitude_images_shared_ms2025')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
    setGratitudeImages(prev => prev.filter(img => img.id !== id));
  };

  // Avoidance Images
  const loadAvoidanceImages = async () => {
    const { data, error } = await supabase
      .from('avoidance_images_shared_ms2025')
      .select('*')
      .order('created_at', { ascending: true });
    
    if (error) throw error;
    setAvoidanceImages(data || []);
  };

  const addAvoidanceImage = async (imageData, filename) => {
    const { data, error } = await supabase
      .from('avoidance_images_shared_ms2025')
      .insert([{ image_url: imageData, filename: filename || 'image.jpg' }])
      .select();
    
    if (error) throw error;
    setAvoidanceImages(prev => [...prev, ...data]);
  };

  const deleteAvoidanceImage = async (id) => {
    const { error } = await supabase
      .from('avoidance_images_shared_ms2025')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
    setAvoidanceImages(prev => prev.filter(img => img.id !== id));
  };

  // Injustice Images
  const loadInjusticeImages = async () => {
    const { data, error } = await supabase
      .from('injustice_images_shared_ms2025')
      .select('*')
      .order('created_at', { ascending: true });
    
    if (error) throw error;
    setInjusticeImages(data || []);
  };

  const addInjusticeImage = async (imageData, filename) => {
    const { data, error } = await supabase
      .from('injustice_images_shared_ms2025')
      .insert([{ image_url: imageData, filename: filename || 'image.jpg' }])
      .select();
    
    if (error) throw error;
    setInjusticeImages(prev => [...prev, ...data]);
  };

  const deleteInjusticeImage = async (id) => {
    const { error } = await supabase
      .from('injustice_images_shared_ms2025')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
    setInjusticeImages(prev => prev.filter(img => img.id !== id));
  };

  // Milestone Images
  const loadMilestoneImages = async () => {
    const { data, error } = await supabase
      .from('milestone_images_shared_ms2025')
      .select('*')
      .order('created_at', { ascending: true });
    
    if (error) throw error;
    setMilestoneImages(data || []);
  };

  const addMilestoneImage = async (imageData, filename) => {
    const { data, error } = await supabase
      .from('milestone_images_shared_ms2025')
      .insert([{ image_url: imageData, filename: filename || 'image.jpg' }])
      .select();
    
    if (error) throw error;
    setMilestoneImages(prev => [...prev, ...data]);
  };

  const deleteMilestoneImage = async (id) => {
    const { error } = await supabase
      .from('milestone_images_shared_ms2025')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
    setMilestoneImages(prev => prev.filter(img => img.id !== id));
  };

  return {
    loading,
    error,
    
    // Visualization
    visualizationImages,
    addVisualizationImage,
    deleteVisualizationImage,
    
    // Gratitude
    gratitudeImages,
    addGratitudeImage,
    deleteGratitudeImage,
    
    // Avoidance
    avoidanceImages,
    addAvoidanceImage,
    deleteAvoidanceImage,
    
    // Injustice
    injusticeImages,
    addInjusticeImage,
    deleteInjusticeImage,
    
    // Milestone
    milestoneImages,
    addMilestoneImage,
    deleteMilestoneImage,
    
    // Utility
    loadAllImageData
  };
};